'use client';
import { Input } from '@ui/components/moleculas';
import {
  IMessagePayload,
  useMessageContext,
  useToast,
  ContextUserType,
  IMessage,
  IMessagesQueryParams,
} from '@ui/components';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useFiles, usePostHogFeatureFlag } from '@ui/hooks';
import { MAX_FILES } from '@ui/hooks/file/constants';
import dynamic from 'next/dynamic';

const RichTextEditor = dynamic(
  () => import('@ui/components/ui/rte-editor/rte-editor').then((mod) => mod.RichTextEditor),
  { ssr: false },
);

interface Props {
  message: IMessage;
  type: 'post' | 'comment' | 'reply';
  userType: ContextUserType;
  query: IMessagesQueryParams;
  main_post_ref: string;
  onDismiss: () => void;
}

export const MessageUpdateForm = ({
  message: initialMessage,
  type,
  userType,
  onDismiss,
  query,
  main_post_ref,
}: Props) => {
  const toast = useToast();
  const messageState = useMessageContext(userType);
  const [message, setMessage] = useState<IMessagePayload>(() => ({
    name: initialMessage.name,
    description: initialMessage.description,
    id: initialMessage.id,
    draft: initialMessage.draft,
    send_email: false, // Initialize send_email for draft editing
  }));
  const isVideoStreamUploadEnabled = usePostHogFeatureFlag('video-stream-upload');
  const { files, onSelectFiles, deleteFile, onDiscardFiles, addInitialFiles, error } = useFiles({
    maxFiles: type === 'post' ? MAX_FILES : 1,
    allowVideos: isVideoStreamUploadEnabled ? true : false,
  });
  const filesInitializedRef = useRef(false);
  const [displayError, setDisplayError] = useState<string | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!filesInitializedRef.current && initialMessage.files) {
      addInitialFiles(initialMessage.files.images || []);
      filesInitializedRef.current = true;
    }
  }, [initialMessage, addInitialFiles]);

  useEffect(() => {
    setMessage((state) => ({ ...state, files: { images: files } }));
  }, [files]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      setDisplayError(error);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = setTimeout(() => {
        setDisplayError(null);
      }, 5000);
    }

    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [error]);
  // Sync draft status when initialMessage changes
  useEffect(() => {
    setMessage((prev) => ({ ...prev, draft: initialMessage.draft }));
  }, [initialMessage.draft]);

  const onSubmit = useCallback(
    async (
      description: string,
      mentions: string[],
      isSaveDraft: boolean,
      clearEditorContent: () => void,
    ) => {
      try {
        const res = await messageState.onUpdateMessage(
          {
            ...message,
            // Ensure drafts never trigger emails
            send_email: isSaveDraft ? false : message.send_email,
            description,
            slug: initialMessage.slug,
            mentions,
            type,
            main_post_ref,
            draft: isSaveDraft,
          },
          query,
        );
        if (res?.data?.id) {
          onDiscardFiles();
          clearEditorContent();
          onDismiss();
        } else {
          toast({ title: `Error updating ${type}, please try again` }, { type: 'error' });
        }
      } catch (e) {
        toast({ title: `Error updating ${type}, please try again` }, { type: 'error' });
      }
    },
    [message, messageState, onDiscardFiles, onDismiss, toast, type],
  );

  const isSubmitButtonDisabled = useMemo(() => {
    if (message.draft) return false;
    if (type === 'post') return !Boolean(message.description) || !Boolean(message.name);
    return !Boolean(message.description);
  }, [message.description, message.name, type, message.draft]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage((prevMessage) => ({ ...prevMessage, name: event.target.value }));
  }, []);

  const handleSendEmailToggle = useCallback((value: boolean) => {
    setMessage((prev) => ({ ...prev, send_email: value }));
  }, []);

  return (
    <div className="flex flex-col gap-1">
      {type === 'post' && (
        <Input
          value={message.name}
          type="text"
          isHideError={true}
          placeholder="Title"
          onChange={handleInputChange}
        />
      )}

      {displayError && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 font-medium">{displayError}</p>
        </div>
      )}

      <RichTextEditor
        deleteFile={deleteFile}
        onSelectFiles={onSelectFiles}
        isSubmitButtonDisabled={isSubmitButtonDisabled}
        onChange={onSubmit}
        value={message.description || ''}
        userType={userType}
        creatorDisplayName={messageState?.community_details?.creator_display_name}
        files={files}
        isLoading={messageState.isCreating || messageState.isPostingImage}
        exceededFileLimit={files.length >= MAX_FILES}
        placeholder={`Update your ${type}...`}
        editorLabel=""
        buttonLabel={message.draft ? 'Post' : 'Update'}
        maxFiles={type === 'post' ? MAX_FILES : 1}
        showCancelButton
        onCancel={onDismiss}
        enableSuggestions
        showSaveDraftButton={type === 'post' && userType === 'creator' && message.draft}
        sendEmail={message.send_email}
        onToggleSendEmail={
          type === 'post' && userType === 'creator' && message.draft
            ? handleSendEmailToggle
            : undefined
        }
        allowVideos={isVideoStreamUploadEnabled ? true : false}
      />
    </div>
  );
};

export default MessageUpdateForm;
